// ── Cart item identity ──────────────────────────────────────────────────
// Product IDs and Combo IDs are independent sequences (both start at 1), so
// a plain numeric id is NOT safe to use as a cart key — a combo #1 and a
// product #1 would collide into the same cart row.
//
// Every cart item therefore carries:
//   id       – namespaced, unique cart key ("product-12" / "combo-3")
//   refId    – the real DB id, used for navigation / API calls
//   itemType – "product" | "combo"
//
// ALL pages that read or write the cart must build/read items through these
// helpers so the key scheme never drifts out of sync between pages again.

export const productCartKey = (id) => `product-${id}`;
export const comboCartKey = (id) => `combo-${id}`;

export const toProductCartItem = (product) => ({
  ...product,
  id: productCartKey(product.id),
  refId: product.id,
  itemType: "product",
  variant_label: product.quantity_unit
    ? (product.quantity_unit.toLowerCase() === "size"
      ? product.quantity
      : `${parseFloat(product.quantity)} ${product.quantity_unit}`)
    : null,
});

export const toComboCartItem = (combo) => ({
  id: comboCartKey(combo.id),
  refId: combo.id,
  itemType: "combo",
  product_name: combo.combo_name,
  product_image: combo.combo_image,
  price: combo.combo_price,
  offer_price: null,
  original_price: combo.original_price,
});

// Read a cart qty for a given raw product/combo id.
export const getCartQtyFor = (cartState, type, id) => {
  const key = type === "combo" ? comboCartKey(id) : productCartKey(id);
  const item = cartState.find((p) => p.id === key);
  return item ? item.qty : 0;
};

// Cart item -> {product|combo, quantity} line the backend expects
// (checkout-initiate, checkout-validate, orders/reserve, courier/calculate,
// order_data.items).
export const toOrderLine = (item) =>
  item.itemType === "combo"
    ? { combo: item.refId, quantity: item.qty }
    : { product: item.refId, quantity: item.qty };

// Backend `reserved_items` / `valid_items` line -> cart item shape.
export const mapReservedToCartItems = (items = []) =>
  items.map((i) => ({
    id: i.type === "combo" ? comboCartKey(i.id) : productCartKey(i.id),
    refId: i.id,
    itemType: i.type,
    qty: i.qty,
    product_name: i.product_name,
    price: Number(i.price),
    offer_price: i.offer_price !== null && i.offer_price !== undefined ? Number(i.offer_price) : null,
    original_price: i.original_price !== null && i.original_price !== undefined ? Number(i.original_price) : null,
    product_image: i.product_image,
    quantity: i.quantity ?? null,
    quantity_unit: i.quantity_unit ?? null,
    stock_quantity: i.stock_quantity ?? i.available_stock ?? null,
    is_available: i.is_available ?? true,
  }));

// Fetch latest details for all items in cart to verify if any offers/prices/stocks have changed.
// Returns { hasChanges: boolean, updatedCart: Array, changeReason: string }
export const verifyCartOffersAndPrices = async (cartItems = [], api) => {
  if (!cartItems || !cartItems.length) {
    return { hasChanges: false, updatedCart: [], changeReason: "" };
  }

  let hasChanges = false;
  let changeReason = "";

  const updatedCart = await Promise.all(
    cartItems.map(async (item) => {
      if (item.itemType === "combo") {
        try {
          const res = await api.get(`combolist/${item.refId}/`);
          const data = res.data?.data;
          if (!data) return item;

          const newPrice = Number(data.combo_price ?? item.price);
          const newOrigPrice = data.original_price ? Number(data.original_price) : item.original_price;
          const newStock = Number(data.available_stock ?? data.stock_quantity ?? item.stock_quantity ?? 0);

          if (Number(item.price) !== newPrice) {
            hasChanges = true;
            changeReason = `Price for "${item.product_name}" changed.`;
          }

          return {
            ...item,
            price: newPrice,
            original_price: newOrigPrice,
            stock_quantity: newStock,
            available_stock: newStock,
            is_available: newStock > 0 && data.is_available !== false,
          };
        } catch {
          return item;
        }
      } else {
        try {
          const res = await api.get(`productlist/${item.refId}/`);
          const data = res.data?.data;
          if (!data) return item;

          let offerPrice =
            data.offer_price !== null && data.offer_price !== undefined && Number(data.offer_price) > 0
              ? Number(data.offer_price)
              : null;
          let offerPct =
            data.offer_percentage !== null && data.offer_percentage !== undefined
              ? Number(data.offer_percentage)
              : null;

          const parentId = data.parent || data.parent_id;
          if (parentId) {
            try {
              const resVar = await api.get(`productvariantfilter/?parent_id=${parentId}`);
              const resolvedParent = resVar.data?.parent_id;
              let varList = resVar.data?.data || [];
              if (resolvedParent && Number(resolvedParent) !== Number(parentId)) {
                const resVar2 = await api.get(`productvariantfilter/?parent_id=${resolvedParent}`);
                varList = resVar2.data?.data || varList;
              }
              const match = varList.find((v) => Number(v.id) === Number(item.refId));
              if (match) {
                offerPrice =
                  match.offer_price !== null && match.offer_price !== undefined && Number(match.offer_price) > 0
                    ? Number(match.offer_price)
                    : null;
                offerPct =
                  match.offer_percentage !== null && match.offer_percentage !== undefined
                    ? Number(match.offer_percentage)
                    : null;
              }
            } catch { }
          }

          if (offerPrice !== null && offerPrice === Number(data.price)) {
            offerPrice = null;
            offerPct = null;
          }

          const basePrice = Number(data.price);
          const newStock = Number(data.stock_quantity ?? data.available_stock ?? item.stock_quantity ?? 0);

          if (item.offer_price !== offerPrice) {
            hasChanges = true;
            if (item.offer_price && !offerPrice) {
              changeReason = `Offer for "${item.product_name}" has ended.`;
            } else if (!item.offer_price && offerPrice) {
              changeReason = `New offer active for "${item.product_name}".`;
            } else {
              changeReason = `Offer price for "${item.product_name}" updated.`;
            }
          } else if (Number(item.price) !== basePrice) {
            hasChanges = true;
            changeReason = `Price for "${item.product_name}" updated.`;
          }

          return {
            ...item,
            price: basePrice,
            offer_price: offerPrice,
            offer_percentage: offerPct,
            stock_quantity: newStock,
            available_stock: newStock,
            is_available: data.is_available !== false && newStock > 0,
          };
        } catch {
          return item;
        }
      }
    })
  );

  return { hasChanges, updatedCart, changeReason };
};